import {
  Box,
  Button,
  Checkbox,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomSelect from "../../../../../components/Select/Select";
import {
  getPetsCompliantSummaryWoLoader,
  updateProcedureByIdWoLoader,
  updateProcedureDetailByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../../util/AppColors";
import { billingTabEnum } from "../../../../../util/enum";
import { isBatchAllowedForFacility } from "../../../../../util/function";
import { refreshClinicStocksToSession } from "../../../../../util/refreshClinicStocksToSession";

const initValues = {
  batchNumber: null,
  batchId: null,
  billableQty: 1,
  uom: null,
  linkToProcedure: "Not linked to a procedure",
  linkProcedureId: null,
  serviceId: null,
  variantId: null,
  billedSeparately: false,
  billingStatus: "Issued",
};
const NOT_LINKED_TO_PROCEDURE = "Not linked to a procedure";

const EditConsumableDrawer = ({ open, onClose, consumable }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initValues);
  const [isSaving, setIsSaving] = useState(false);

  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicDet, services, locations, facilities } =
    useMemo(() => {
      if (!mandateDataRaw) {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
      try {
        const reqData = JSON.parse(mandateDataRaw);
        return {
          clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
          clinicDet: reqData?.clinic ?? {},
          services: Array.isArray(reqData?.services) ? reqData.services : [],
          locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
          facilities: Array.isArray(reqData?.facilities)
            ? reqData.facilities
            : [],
        };
      } catch {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
    }, [mandateDataRaw]);

  const getReqFacility = () =>
    facilities?.find((fac) => fac?.facilityId === appDet?.facilityId) ?? null;

  const getBatches = (stkId = null) => {
    const reqStock = clinicStocks?.find((stc) => stc?.stockId === stkId);
    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqList = clinicBatches
      ?.filter((bat) =>
        isBatchAllowedForFacility(bat, getReqFacility(), locations),
      )
      ?.filter((li) => li?.quantity > 0)
      ?.sort((a, b) => a?.quantity - b?.quantity);
    return reqList?.length > 0
      ? reqList?.map((bat) => {
          const reqLocation = locations?.find(
            (loc) => loc?.id === bat?.locationId,
          );
          return {
            name: bat?.batchNo,
            value: bat?.batchId,
            subunits: bat?.subunits,
            quantity: bat?.quantity,
            locationId: bat?.locationId,
            locationName: reqLocation?.locationName ?? "",
            uom: bat?.qtyMapName ?? "",
          };
        })
      : [
          {
            name: "Unknown",
            value: null,
            subunits: null,
            quantity: null,
            locationId: null,
            locationName: "",
            uom: "",
          },
        ];
  };

  const getBatchByStockAndBatchId = (stkId, batId) => {
    if (!stkId || !batId) return null;
    const stockMat = clinicStocks?.find((stc) => stc?.stockId === stkId);
    return (
      stockMat?.stockBatch
        ?.filter((btch) => btch?.createdBy === clinicDet?.id)
        ?.find((bat) => bat?.batchId === batId) ?? null
    );
  };

  const buildBatchOption = (stkId, batchId = null, batchNo = null) => {
    const reqBat = batchId ? getBatchByStockAndBatchId(stkId, batchId) : null;
    const reqLocation = locations?.find(
      (loc) => loc?.id === reqBat?.locationId,
    );
    return {
      name: batchNo ?? reqBat?.batchNo ?? "Unknown",
      value: batchId ?? reqBat?.batchId ?? null,
      subunits: reqBat?.subunits ?? null,
      quantity: reqBat?.quantity ?? null,
      locationId: reqBat?.locationId ?? null,
      locationName: reqLocation?.locationName ?? "",
      uom: reqBat?.qtyMapName ?? "",
    };
  };

  const getBatchesForSelect = (
    stkId = null,
    batchId = null,
    batchNo = null,
  ) => {
    const batchList = getBatches(stkId);
    if (!batchId && !batchNo) return batchList;

    const exists = batchList?.some(
      (bat) =>
        (batchId && String(bat?.value) === String(batchId)) ||
        (batchNo && bat?.name === batchNo),
    );
    if (exists) return batchList;

    const selectedOption = buildBatchOption(stkId, batchId, batchNo);
    if (
      batchList?.length === 1 &&
      batchList?.[0]?.name === "Unknown" &&
      !batchList?.[0]?.value
    ) {
      return [selectedOption];
    }
    return [selectedOption, ...(batchList ?? [])];
  };

  const getUOMListByStockId = (stkId = null) => {
    if (!stkId) return [];
    const stockMat = clinicStocks?.find((stc) => stc?.stockId === stkId);
    return (
      stockMat?.uomMappings
        ?.filter((ml) => ml?.allowSale === "Y" && ml?.show)
        ?.map((ml) => ({
          name: ml?.unit,
          value: ml?.unit,
          conversion: ml?.conversion,
          uomMappingId: ml?.uomMappingId ?? null,
        })) ?? []
    );
  };

  const getProcedureListAsDrop = () => {
    if (!reqDet?.procedures?.length) return [];
    const reqProcedures = reqDet.procedures
      .filter((li) => li?.serviceId && li?.procedureVariantId)
      .map((prcd) => ({
        name: prcd?.procedureName,
        value: prcd?.procedureName,
        procedureId: prcd?.procedureId,
        serviceId: prcd?.serviceId,
        variantId: prcd?.procedureVariantId,
      }));
    reqProcedures.unshift({
      name: "Not linked to a procedure",
      value: "Not linked to a procedure",
      serviceId: null,
      variantId: null,
    });
    return reqProcedures;
  };

  const resolveInitialBatchNumber = (item) => {
    if (!item?.stockId) return null;

    const batchId = item?.batchId ?? null;
    const batchNo = item?.batchNo ?? null;
    const batchList = getBatchesForSelect(item?.stockId, batchId, batchNo);

    if (batchId || batchNo) {
      const match = batchList?.find(
        (bat) =>
          (batchId && String(bat?.value) === String(batchId)) ||
          (batchNo && bat?.name === batchNo),
      );
      if (match) return match;
      return buildBatchOption(item?.stockId, batchId, batchNo);
    }

    return batchList?.[0] ?? null;
  };

  const isRequested = consumable?.status === "requested";

  const isUsed =
    consumable?.status === true ||
    consumable?.status === "true" ||
    consumable?.remarks === "Completed" ||
    values?.billingStatus === "Used";

  const isIssued =
    !isRequested &&
    !isUsed &&
    (consumable?.status === "issued" ||
      consumable?.status === true ||
      consumable?.status === "true" ||
      !!consumable?.batchId);

  const hideBillSeparately = consumable?.billing ?? false;

  const onChangeValue = (name, value, addObj) => {
    setValues((prev) => {
      const reqObj = { ...prev, [name]: value };
      if (name === "batchNumber") {
        reqObj.batchNumber = value;
        reqObj.batchId = value?.value ?? null;
      }
      if (name === "linkToProcedure") {
        reqObj.linkToProcedure = value;
        reqObj.linkProcedureId = addObj?.procedureId ?? null;
        reqObj.serviceId = addObj?.serviceId ?? null;
        reqObj.variantId = addObj?.variantId ?? null;
      }
      return reqObj;
    });
  };

  useEffect(() => {
    if (!open) {
      setValues(initValues);
      setIsSaving(false);
      return;
    }
    if (!consumable) return;

    const isLinked = consumable?.linked === "Y";
    const isItemRequested =
      consumable?.status === "requested" ||
      consumable?.remarks === "Requested" ||
      consumable?.requestBadgeText;
    const isItemUsed =
      consumable?.status === true ||
      consumable?.status === "true" ||
      consumable?.remarks === "Completed";
    const reqUomList = getUOMListByStockId(consumable?.stockId);
    const matUom = reqUomList?.find(
      (ul) =>
        ul?.uomMappingId === consumable?.uomId ||
        ul?.value === consumable?.uomName,
    );
    const batchNumber = resolveInitialBatchNumber(consumable);
    setValues({
      batchNumber,
      batchId: batchNumber?.value ?? consumable?.batchId ?? null,
      billableQty: consumable?.quantity ?? 1,
      uom: consumable?.uomName?.length > 0 && matUom ? matUom : null,
      linkToProcedure: isLinked
        ? (consumable?.procedure?.procedureName ?? "Not linked to a procedure")
        : "Not linked to a procedure",
      linkProcedureId: isLinked
        ? (consumable?.procedure?.procedureId ?? null)
        : null,
      serviceId: isLinked
        ? (consumable?.procedure?.serviceId ?? consumable?.serviceId ?? null)
        : null,
      variantId: isLinked
        ? (consumable?.procedure?.procedureVariantId ??
          consumable?.procedureVariantId ??
          null)
        : null,
      billedSeparately: !!consumable?.billSeparated,
      billingStatus: isItemRequested
        ? "Requested"
        : isItemUsed
          ? "Used"
          : "Issued",
    });
  }, [consumable, open]);

  const onApiCall = () => {
    dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
  };

  const buildConsumableDetail = () => {
    const reqStock = clinicStocks?.find(
      (stc) => stc?.stockId === consumable?.stockId,
    );
    const selectedBatchId =
      values?.batchNumber?.value ?? consumable?.batchId ?? null;
    const reqBatDet = getBatchByStockAndBatchId(
      consumable?.stockId,
      selectedBatchId,
    );
    const uomList = getUOMListByStockId(consumable?.stockId);
    const matchedUom =
      values?.uom ??
      uomList?.find(
        (ul) =>
          ul?.uomMappingId === consumable?.uomId ||
          ul?.value === consumable?.uomName,
      );
    const batUomMatch = uomList?.find(
      (ul) =>
        ul?.uomMappingId === reqBatDet?.mapId ||
        ul?.value === reqBatDet?.uomMapName,
    );
    const linkedProcedure = values?.linkToProcedure;
    const isLinkedToProcedure = linkedProcedure !== "Not linked to a procedure";
    const linkToProcedure = isLinkedToProcedure;
    const isNoAmount = values?.billedSeparately
      ? false
      : reqStock?.autoConsumed || !reqStock?.billable || linkToProcedure;

    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqBat = reqStock?.autoConsumed
      ? clinicBatches?.[0]
      : selectedBatchId
        ? (clinicBatches?.find((bat) => bat?.batchId === selectedBatchId) ??
          clinicBatches?.[0])
        : clinicBatches?.[0];
    const currLocat = locations?.find((loc) => loc?.id === reqBat?.locationId);
    const stckUnitPrice =
      reqBat?.unitSellingPrice ?? clinicBatches?.[0]?.unitSellingPrice ?? 0;
    const batchConversion = Number(batUomMatch?.conversion ?? 1);
    const selectedConversion = Number(matchedUom?.conversion ?? 1);
    const itmPrice =
      batUomMatch?.uomMappingId === matchedUom?.uomMappingId
        ? Number(stckUnitPrice)
        : (Number(stckUnitPrice) * batchConversion) / selectedConversion;
    const reqUnitPrice = isNoAmount ? 0 : itmPrice;

    const selectedProc =
      (values?.linkProcedureId != null
        ? reqDet?.procedures?.find(
            (pl) => String(pl?.procedureId) === String(values.linkProcedureId),
          )
        : null) ??
      reqDet?.procedures?.find((pl) => pl?.procedureName === linkedProcedure);
    const serviceId = isLinkedToProcedure
      ? (selectedProc?.serviceId ?? values?.serviceId)
      : (consumable?.procedure?.serviceId ?? consumable?.serviceId ?? null);
    const reqServ = services?.find((ser) => ser?.serviceId === serviceId);
    const reqProced = isLinkedToProcedure
      ? selectedProc
      : (consumable?.procedure ??
        reqDet?.procedures?.find((pl) => pl?.serviceId === serviceId));
    const reqVar = reqServ?.serviceVariants?.find(
      (vnt) =>
        String(vnt?.variantId) ===
        String(reqProced?.procedureVariantId ?? consumable?.procedureVariantId),
    );
    const taxPerent =
      reqServ?.taxApplicability?.toLowerCase() === "yes"
        ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
        : 0;
    const reqPercent = 100 + Number(taxPerent);
    const unitPrice = Number(reqUnitPrice) / (reqPercent / 100);
    const prdTaxValue = (taxPerent / 100) * Number(unitPrice);
    const quantity = Number(values?.billableQty) || 1;
    const markUsed = values?.billingStatus === "Used";
    const isAlreadyTrue =
      consumable?.status === true || consumable?.status === "true";
    const isRequestedSave =
      !isAlreadyTrue &&
      (consumable?.status === "requested" ||
        consumable?.remarks === "Requested" ||
        (!selectedBatchId &&
          !reqBat?.batchId &&
          consumable?.status === "requested"));

    return {
      detailId: consumable?.detailId ?? null,
      stockId: consumable?.stockId ?? null,
      batchId:
        selectedBatchId ?? (reqStock?.autoConsumed ? reqBat?.batchId : null),
      type: "Consumables",
      quantity,
      status:
        isAlreadyTrue || markUsed
          ? true
          : isRequestedSave
            ? "requested"
            : reqStock?.autoConsumed
              ? consumable?.detailId
                ? consumable?.status
                : true
              : (selectedBatchId ?? reqBat?.batchId)
                ? (consumable?.status ?? true)
                : "requested",
      remarks: isAlreadyTrue
        ? consumable?.remarks === "Completed"
          ? "Completed"
          : (consumable?.remarks ?? "")
        : isRequestedSave
          ? "Requested"
          : markUsed
            ? "Requested"
            : consumable?.remarks === "Completed"
              ? "Completed"
              : (consumable?.remarks ?? ""),
      estimatedCost: isNoAmount
        ? 0
        : Number(reqUnitPrice) * Number(quantity ?? 1),
      name: consumable?.procedureName ?? consumable?.name,
      procedureName: consumable?.procedureName ?? consumable?.name,
      isDelete: false,
      serviceId,
      tax: isNoAmount ? 0 : Number(prdTaxValue ?? 0) * Number(quantity ?? 1),
      total: isNoAmount ? 0 : Number(reqUnitPrice) * Number(quantity ?? 1),
      mrpPrice: isNoAmount ? 0 : Number(reqUnitPrice ?? 0),
      procedureVariantId: reqVar?.variantId ?? reqProced?.procedureVariantId,
      patientWorkbook: true,
      mainProcedure: false,
      drugSchedule: reqServ?.drugSchedule ?? null,
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      packageUom: reqBat?.salesUom ?? consumable?.packageUom ?? null,
      expiryDate: reqBat?.expiryDate ?? consumable?.expiryDate ?? null,
      batchNo:
        values?.batchNumber?.name ||
        reqBat?.batchNo ||
        consumable?.batchNo ||
        null,
      billable: reqStock?.billable,
      autoConsumed: reqStock?.autoConsumed,
      linked: linkToProcedure ? "Y" : "N",
      billSeparated: !!values?.billedSeparately,
      billType: reqStock?.autoConsumed
        ? currLocat?.category
          ? billingTabEnum?.[currLocat?.category]
          : "treatment"
        : "treatment",
      uomId: matchedUom?.uomMappingId ?? consumable?.uomId ?? null,
      uomName: matchedUom?.value ?? consumable?.uomName ?? null,
      reqProced,
      reqServ,
      reqVar,
      billing: consumable?.billing ?? false,
      locationId:
        values?.batchNumber?.locationId ??
        reqBat?.locationId ??
        currLocat?.id ??
        null,
      locationName:
        values?.batchNumber?.locationName ?? currLocat?.locationName ?? null,
    };
  };

  const getCurrentLinkName = () =>
    typeof values?.linkToProcedure === "object"
      ? values?.linkToProcedure?.value
      : values?.linkToProcedure;

  const getSelectedProcedureByLink = () => {
    const linkName = getCurrentLinkName();
    if (!linkName || linkName === NOT_LINKED_TO_PROCEDURE) return null;
    if (values?.linkProcedureId != null) {
      return (
        reqDet?.procedures?.find(
          (pl) => String(pl?.procedureId) === String(values.linkProcedureId),
        ) ?? null
      );
    }
    return (
      reqDet?.procedures?.find((pl) => pl?.procedureName === linkName) ?? null
    );
  };

  const buildProcedureUpdateData = (reqProced, reqServ, reqVar, reqObj) => {
    let tax = 0;
    let total = reqVar?.servicePrice ?? reqProced?.serviceCharge ?? 0;
    if (reqServ?.taxApplicability?.toLowerCase() === "yes") {
      tax +=
        (Number(reqVar?.servicePrice ?? 0) / 100) * Number(reqServ?.tax ?? 0);
      total += tax;
    }

    return {
      serviceCharge: reqProced?.serviceCharge,
      serviceType: reqProced?.serviceType,
      appointmentId: reqDet?.appointment?.appointmentId,
      procedureDetails: [reqObj],
      tax: Number(Number(tax) * Number(reqProced?.quantity ?? 1)),
      total: Number(Number(total) * Number(reqProced?.quantity ?? 1)),
      mrpPrice: Number(reqVar?.servicePrice ?? 0),
      procedureVariantId: reqVar?.variantId,
      quantity: Number(reqProced?.quantity ?? 1),
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      paymentId: reqDet?.paymentId ?? null,
      doseInSeries: reqProced?.doseInSeries ?? null,
      serviceRemainderId: reqProced?.serviceRemainderId ?? null,
    };
  };

  const handleSave = () => {
    if (!consumable) return;

    const detailPayload = buildConsumableDetail();
    const { reqProced, reqServ, reqVar, ...reqObj } = detailPayload;
    const currentLinkName = getCurrentLinkName();
    const isNotLinkedSelection = currentLinkName === NOT_LINKED_TO_PROCEDURE;
    const selectedTargetProc = getSelectedProcedureByLink();
    const sourceProcedureId = consumable?.procedure?.procedureId ?? null;
    const targetProcedureId = selectedTargetProc?.procedureId ?? null;
    const shouldMoveDetail =
      !!selectedTargetProc &&
      String(targetProcedureId ?? "") !== String(sourceProcedureId ?? "");

    setIsSaving(true);
    const finalize = (success) => {
      setIsSaving(false);
      if (success) {
        refreshClinicStocksToSession(dispatch);
        onApiCall();
        onClose();
      }
    };

    const runOldUpdate = (procedureId, nextReqObj = reqObj) => {
      if (!procedureId || !reqProced?.procedureId || !reqServ) {
        finalize(false);
        return;
      }
      dispatch(
        updateProcedureByIdWoLoader({
          id: procedureId,
          data: buildProcedureUpdateData(
            reqProced,
            reqServ,
            reqVar,
            nextReqObj,
          ),
        }),
      ).then((res) => finalize(!!res?.payload));
    };

    if (!shouldMoveDetail) {
      const updateProcedureId = isNotLinkedSelection
        ? sourceProcedureId
        : (targetProcedureId ?? sourceProcedureId);
      runOldUpdate(updateProcedureId);
      return;
    }

    dispatch(
      updateProcedureDetailByIdWoLoader({
        procedureId: targetProcedureId,
        procedureDetailId: reqObj?.detailId,
      }),
    ).then((res) => {
      if (!res?.payload) {
        finalize(false);
        return;
      }

      const payload = res?.payload?.data ?? res?.payload ?? {};
      const nextProcedureId =
        payload?.procedureId ?? payload?.id ?? targetProcedureId;
      const nextDetailId = payload?.detailId ?? reqObj?.detailId;

      runOldUpdate(nextProcedureId, {
        ...reqObj,
        detailId: nextDetailId,
      });
    });
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Edit item"
      modal
      modalWidth={40}
      reduceHeaderSize
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        <Box p={3} flexGrow={1} overflow="auto">
          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              ITEM
            </Typography>
            <TextField
              fullWidth
              size="small"
              disabled
              value={consumable?.procedureName ?? consumable?.name ?? ""}
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            />
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                BATCH / LOT NO.
              </Typography>
              <CustomSelect
                list={getBatchesForSelect(
                  consumable?.stockId,
                  values?.batchNumber?.value ?? consumable?.batchId,
                  values?.batchNumber?.name ?? consumable?.batchNo,
                )}
                value={values?.batchNumber}
                handleChange={(e, value) => onChangeValue("batchNumber", value)}
                name="batchNumber"
                search
                isBatchNumber
                disabled={
                  hideBillSeparately ||
                  !consumable?.stockId ||
                  clinicStocks?.find(
                    (stc) => stc?.stockId === consumable?.stockId,
                  )?.autoConsumed
                }
              />
            </Grid>
            <Grid item xs={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                BILLABLE QTY
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={values?.billableQty}
                onChange={(e) => onChangeValue("billableQty", e.target.value)}
                inputProps={{ min: 1 }}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                }}
                // disabled={hideBillSeparately}
              />
            </Grid>
            <Grid item xs={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                UOM
              </Typography>
              <CustomSelect
                list={getUOMListByStockId(consumable?.stockId)}
                value={values?.uom}
                search
                handleChange={(e, value) => onChangeValue("uom", value)}
              />
            </Grid>
          </Grid>

          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              LINK TO PROCEDURE
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={values?.linkToProcedure}
              onChange={(e) => {
                const selected = getProcedureListAsDrop().find(
                  (opt) => opt.value === e.target.value,
                );
                onChangeValue("linkToProcedure", e.target.value, {
                  procedureId: selected?.procedureId ?? null,
                  serviceId: selected?.serviceId ?? null,
                  variantId: selected?.variantId ?? null,
                });
              }}
              sx={{
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            >
              {getProcedureListAsDrop().map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.name}
                </MenuItem>
              ))}
            </TextField>
            <Typography sx={{ fontSize: "12px", color: "#757575", mt: 0.8 }}>
              This is a clinical record of which procedure the item was used
              for.
            </Typography>
          </Box>

          {!hideBillSeparately ? (
            <Box mb={2}>
              <div className="flex-row">
                <Checkbox
                  checked={!!values?.billedSeparately}
                  onChange={(e) =>
                    onChangeValue("billedSeparately", e.target.checked)
                  }
                  sx={{
                    color: AppColors.appPrimary,
                    "&.Mui-checked": { color: AppColors.appPrimary },
                  }}
                />
                <div className="ml10">
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#212121",
                      mt: -0.3,
                    }}
                  >
                    Bill separately (extra line item)
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#757575" }}>
                    Suggested automatically — override if this case needs
                    something different.
                  </Typography>
                </div>
              </div>
            </Box>
          ) : null}

          <Box mb={2}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              BILLING STATUS
            </Typography>
            <Box
              display="flex"
              gap={1}
              mb={1}
              alignItems="center"
              flexWrap="wrap"
            >
              {isRequested ? (
                <Box
                  sx={{
                    backgroundColor: "#fff3e0",
                    color: "#e65100",
                    fontSize: "12px",
                    fontWeight: 600,
                    px: 1.2,
                    py: 0.3,
                    borderRadius: "4px",
                    border: "1px solid #ffe082",
                  }}
                >
                  Requested
                </Box>
              ) : isUsed ? (
                <Box
                  sx={{
                    backgroundColor: "#e8f5e9",
                    color: "#2e7d32",
                    fontSize: "12px",
                    fontWeight: 600,
                    px: 1.2,
                    py: 0.3,
                    borderRadius: "4px",
                    border: "1px solid #c8e6c9",
                  }}
                >
                  Used
                </Box>
              ) : isIssued ? (
                <>
                  <Box
                    sx={{
                      backgroundColor: "#e0f2fe",
                      color: "#0284c7",
                      fontSize: "12px",
                      fontWeight: 600,
                      px: 1.2,
                      py: 0.3,
                      borderRadius: "4px",
                      border: "1px solid #bae6fd",
                    }}
                  >
                    Issued
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onChangeValue("billingStatus", "Used")}
                    sx={{
                      backgroundColor: "#003e7e",
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "12px",
                      height: "30px",
                      px: 1.5,
                      boxShadow: "none",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        backgroundColor: "#002b58",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Mark Consumed
                  </Button>
                </>
              ) : null}
            </Box>
          </Box>

          {hideBillSeparately ? (
            <Typography
              sx={{ fontSize: "12px", color: "#757575", mb: 2, mt: -1 }}
            >
              Requested from pharmacy — billed on a separate invoice, not part
              of the clinic total.
            </Typography>
          ) : null}
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <Button
            onClick={onClose}
            sx={{
              color: "#616161",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
            }}
          >
            Cancel
          </Button>
          <CustomButton
            text="Save changes"
            smallBtn
            disabled={isSaving}
            onClick={handleSave}
          />
        </Box>
      </Box>
    </CustomModal>
  );
};

export default EditConsumableDrawer;
